import React from "react";

const TermsOfService = () => {
  return (
    <div className="bg-white text-black px-6 py-16 max-w-3xl mx-auto font-sans">
      <h1 className="text-3xl font-bold mb-6">Terms and Conditions</h1>

      <p className="text-sm text-black/80 mb-4">Last Updated: April 21, 2025</p>

      <p className="text-sm text-black/80 mb-4">
        Welcome to FullPlateNow (the “Platform”), a business-to-business
        software-as-a-service platform operated by FullPlateNow LLC
        (“FullPlateNow,” “we,” “us,” or “our”). These Terms and Conditions
        (“Terms”) govern your access to and use of the FullPlateNow website
        located at [www.fullplatenow.com] (the “Website”) and the services
        provided through the Platform (collectively, the “Services”). The
        Services are designed exclusively for independent restaurants
        (“Restaurants,” “you,” or “your”) to manage their entire operations on
        third-party delivery services, including but not limited to DoorDash,
        Grubhub, Uber Eats, and Postmates (collectively, “Delivery Services”).
        FullPlateNow manages all aspects of the Restaurant’s presence on these
        Delivery Services, including setting and adjusting menu prices to
        incorporate our service fee, managing menus, order management,
        analytics, and communication with Delivery Services representatives. Our
        service fee is structured as a 50-60% upcharge on menu prices, paid by
        customers through the Delivery Services, ensuring Restaurants retain
        100% of their base menu price. By accessing or using the Website or
        Services, you agree to be bound by these Terms in their entirety. If you
        do not agree to these Terms, you must not access or use the Website or
        Services.
      </p>

      <h2 className="text-lg font-semibold mt-6 mb-2">
        1. Acceptance of Terms
      </h2>
      <p className="text-sm mb-4">
        By accessing or using the Website or Services, you represent that you
        are a duly authorized representative of a Restaurant with the authority
        to enter into this agreement on behalf of the Restaurant. You
        acknowledge that you have read, understood, and agree to be bound by
        these Terms, as well as our Privacy Policy, which is incorporated herein
        by reference. We reserve the right to modify these Terms at any time,
        and such modifications will be effective upon posting on the Website.
        Your continued use of the Services following any modification
        constitutes your acceptance of the updated Terms.
      </p>

      <h2 className="text-lg font-semibold mt-6 mb-2">2. Nature of Services</h2>
      <p className="text-sm mb-4">
        FullPlateNow provides a SaaS platform that manages the entire presence
        of Restaurants on Delivery Services, including but not limited to:
      </p>
      <ul className="text-sm mb-4 list-disc pl-5">
        <li>
          Setting and adjusting menu prices on Delivery Services to incorporate
          a 50-60% upcharge as our service fee, which is paid by customers
          through the Delivery Services.
        </li>
        <li>
          Managing menus, including uploading, updating, and syncing menu items
          with Delivery Services.
        </li>
        <li>
          Aggregating and processing orders placed through Delivery Services,
          including order acceptance, denial, and tracking.
        </li>
        <li>
          Providing analytics and financial reports on order volume, revenue,
          and performance across Delivery Services.
        </li>
        <li>
          Facilitating communication between Restaurants and Delivery Services
          representatives or drivers.
        </li>
      </ul>
      <p className="text-sm mb-4">
        FullPlateNow does not directly interact with end customers; all orders
        are placed through Delivery Services, and FullPlateNow facilitates the
        management of such orders for Restaurants. FullPlateNow retains the
        difference between the upcharged menu price and the amounts paid to
        Restaurants and Delivery Services, typically $8-9 per $50 order, as its
        service fee.
      </p>

      <h2 className="text-lg font-semibold mt-6 mb-2">3. Eligibility</h2>
      <p className="text-sm mb-4">To use the Services, you must:</p>
      <ul className="text-sm mb-4 list-disc pl-5">
        <li>
          Be a legally operating Restaurant with the necessary licenses and
          permits to conduct business.
        </li>
        <li>
          Have an existing relationship with one or more Delivery Services.
        </li>
        <li>
          Be at least 18 years of age and have the legal capacity to enter into
          contracts.
        </li>
        <li>
          Not be barred from using the Services under applicable law or by any
          prior agreement with FullPlateNow.
        </li>
      </ul>
      <p className="text-sm mb-4">
        We reserve the right to refuse access to the Services to any Restaurant
        at our sole discretion.
      </p>

      <h2 className="text-lg font-semibold mt-6 mb-2">
        4. Account Registration and Security
      </h2>
      <p className="text-sm mb-4">
        To access the Services, you must create an account by completing the
        registration process on the Website, which includes providing accurate
        and complete information about your Restaurant (“Account”). You agree
        to:
      </p>
      <ul className="text-sm mb-4 list-disc pl-5">
        <li>
          Maintain the confidentiality of your Account credentials (e.g.,
          username, password).
        </li>
        <li>
          Notify us immediately of any unauthorized use of your Account or other
          security breaches.
        </li>
        <li>
          Be solely responsible for all activities conducted through your
          Account.
        </li>
      </ul>
      <p className="text-sm mb-4">
        FullPlateNow is not liable for any loss or damage arising from your
        failure to safeguard your Account credentials. We reserve the right to
        suspend or terminate your Account if we suspect fraudulent, abusive, or
        unauthorized activity.
      </p>

      <h2 className="text-lg font-semibold mt-6 mb-2">
        5. License to Use the Services
      </h2>
      <p className="text-sm mb-4">
        Subject to your compliance with these Terms, FullPlateNow grants you a
        limited, non-exclusive, non-transferable, revocable license to access
        and use the Services solely for the purpose of managing your
        Restaurant’s operations on Delivery Services. This license does not
        include any right to:
      </p>
      <ul className="text-sm mb-4 list-disc pl-5">
        <li>
          Modify, copy, distribute, or create derivative works of the Services
          or any content therein.
        </li>
        <li>
          Sell, lease, sublicense, or otherwise transfer access to the Services.
        </li>
        <li>Use the Services for any unlawful or unauthorized purpose.</li>
      </ul>
      <p className="text-sm mb-4">
        We reserve all rights not expressly granted herein.
      </p>

      <h2 className="text-lg font-semibold mt-6 mb-2">6. Fees and Payment</h2>
      <h3 className="text-base font-semibold mt-4 mb-2">
        6.1 Service Fee Structure
      </h3>
      <p className="text-sm mb-4">
        FullPlateNow manages menu pricing on Delivery Services by applying a
        50-60% upcharge to your base menu prices, which serves as our service
        fee. This upcharge is paid by customers through the Delivery Services.
        For example, if your base menu price for an item is $10, FullPlateNow
        may set the price at $15-$16 on the Delivery Services, ensuring you
        receive the full $10 while FullPlateNow retains the difference as its
        service fee, typically yielding $8-9 per $50 order after accounting for
        payments to you and the Delivery Services.
      </p>

      <h3 className="text-base font-semibold mt-4 mb-2">
        6.2 Payment Collection
      </h3>
      <p className="text-sm mb-4">
        FullPlateNow collects the upcharge revenue through agreements with
        Delivery Services, who process customer payments and remit the upcharge
        proceeds to FullPlateNow on a monthly basis, as per separate agreements
        with each Delivery Service.
      </p>

      <h3 className="text-base font-semibold mt-4 mb-2">
        6.3 Restaurant Payouts
      </h3>
      <p className="text-sm mb-4">
        FullPlateNow facilitates direct deposits to Restaurants for their base
        menu price earnings (100% of the menu price before upcharge) as received
        from Delivery Services. Payouts are processed on a schedule determined
        by FullPlateNow and communicated to you, subject to any applicable fees
        for payment processing, which will be disclosed in advance.
      </p>

      <h3 className="text-base font-semibold mt-4 mb-2">6.4 Taxes</h3>
      <p className="text-sm mb-4">
        You are responsible for all taxes associated with your base menu price
        earnings, including sales, use, or value-added taxes, except for taxes
        on FullPlateNow’s income. FullPlateNow will collect and remit applicable
        taxes on the upcharge service fee as required by law.
      </p>

      <h2 className="text-lg font-semibold mt-6 mb-2">
        7. Restaurant Responsibilities
      </h2>
      <p className="text-sm mb-4">You agree to:</p>
      <ul className="text-sm mb-4 list-disc pl-5">
        <li>
          Provide accurate and up-to-date information about your Restaurant,
          including menu items, base pricing, and availability, for FullPlateNow
          to manage and sync with Delivery Services.
        </li>
        <li>
          Ensure compliance with all agreements, terms, and conditions imposed
          by the Delivery Services.
        </li>
        <li>
          Maintain all necessary licenses, permits, and insurance required to
          operate your Restaurant and fulfill orders.
        </li>
        <li>
          Respond promptly to orders, driver updates, and communications
          facilitated through the Services.
        </li>
        <li>
          Indemnify FullPlateNow for any claims arising from your failure to
          fulfill orders, comply with Delivery Service requirements, or due to
          inaccuracies in the information you provide.
        </li>
      </ul>

      <h2 className="text-lg font-semibold mt-6 mb-2">
        8. Intellectual Property
      </h2>
      <h3 className="text-base font-semibold mt-4 mb-2">8.1 Ownership</h3>
      <p className="text-sm mb-4">
        The Services, Website, and all content therein (including but not
        limited to text, graphics, logos, software, and data) are the exclusive
        property of FullPlateNow or its licensors and are protected by
        copyright, trademark, and other intellectual property laws. You may not
        use, reproduce, or distribute any content from the Services without our
        prior written consent.
      </p>

      <h3 className="text-base font-semibold mt-4 mb-2">
        8.2 Restaurant Content
      </h3>
      <p className="text-sm mb-4">
        By uploading content to the Services (e.g., menu items, images,
        descriptions), you grant FullPlateNow a worldwide, non-exclusive,
        royalty-free, transferable license to use, reproduce, modify (for
        pricing adjustments), and distribute such content solely for the purpose
        of providing the Services and syncing with Delivery Services. You
        represent and warrant that you have all necessary rights to grant this
        license and that your content does not infringe on any third-party
        rights.
      </p>

      <h2 className="text-lg font-semibold mt-6 mb-2">
        9. Data Privacy and Security
      </h2>
      <h3 className="text-base font-semibold mt-4 mb-2">9.1 Data Collection</h3>
      <p className="text-sm mb-4">
        FullPlateNow collects and processes data related to your Restaurant’s
        operations, including order details, financial data, and communications,
        as necessary to provide the Services. Our Privacy Policy governs the
        collection and use of this data.
      </p>

      <h3 className="text-base font-semibold mt-4 mb-2">9.2 Data Security</h3>
      <p className="text-sm mb-4">
        FullPlateNow implements reasonable measures to protect your data from
        unauthorized access, disclosure, or loss. However, you acknowledge that
        no system is entirely secure, and FullPlateNow is not liable for any
        data breaches unless caused by our gross negligence or willful
        misconduct.
      </p>

      <h3 className="text-base font-semibold mt-4 mb-2">
        9.3 Data Sharing with Delivery Services
      </h3>
      <p className="text-sm mb-4">
        FullPlateNow shares necessary data (e.g., menus, adjusted pricing, order
        statuses) with Delivery Services to facilitate order fulfillment and
        pricing management. You consent to this sharing as part of using the
        Services.
      </p>

      <h2 className="text-lg font-semibold mt-6 mb-2">10. Confidentiality</h2>
      <h3 className="text-base font-semibold mt-4 mb-2">10.1 Definition</h3>
      <p className="text-sm mb-4">
        “Confidential Information” includes any non-public information disclosed
        by either party, including business plans, financial data, and
        operational details, whether disclosed orally or in writing.
      </p>

      <h3 className="text-base font-semibold mt-4 mb-2">10.2 Obligations</h3>
      <p className="text-sm mb-4">
        Each party agrees to maintain the confidentiality of the other party’s
        Confidential Information, using at least the same degree of care as it
        uses for its own confidential information, but no less than reasonable
        care. Confidential Information may only be disclosed to employees,
        contractors, or Delivery Services who need to know such information to
        perform under these Terms and are bound by confidentiality obligations.
      </p>

      <h3 className="text-base font-semibold mt-4 mb-2">10.3 Exceptions</h3>
      <p className="text-sm mb-4">
        Confidential Information does not include information that is publicly
        available, already known by the receiving party, or independently
        developed without reference to the disclosing party’s information.
      </p>

      <h2 className="text-lg font-semibold mt-6 mb-2">
        11. Limitation of Liability
      </h2>
      <h3 className="text-base font-semibold mt-4 mb-2">
        11.1 Disclaimer of Warranties
      </h3>
      <p className="text-sm mb-4">
        The Services are provided “as is” and “as available” without warranties
        of any kind, express or implied, including warranties of
        merchantability, fitness for a particular purpose, or non-infringement.
        FullPlateNow does not guarantee that the Services will be uninterrupted,
        error-free, or free from harmful components. FullPlateNow is not
        responsible for the actions or performance of Delivery Services,
        including but not limited to pricing disputes, order fulfillment delays,
        or customer complaints.
      </p>

      <h3 className="text-base font-semibold mt-4 mb-2">
        11.2 Exclusion of Damages
      </h3>
      <p className="text-sm mb-4">
        To the maximum extent permitted by law, FullPlateNow shall not be liable
        for any indirect, incidental, special, consequential, or punitive
        damages, including but not limited to loss of profits, revenue, data, or
        goodwill, arising out of or relating to your use of the Services, even
        if advised of the possibility of such damages.
      </p>

      <h3 className="text-base font-semibold mt-4 mb-2">
        11.3 Cap on Liability
      </h3>
      <p className="text-sm mb-4">
        FullPlateNow’s total liability for any claims arising under these Terms
        shall not exceed the total upcharge service fees collected on your
        behalf in the six (6) months preceding the claim.
      </p>

      <h2 className="text-lg font-semibold mt-6 mb-2">12. Indemnification</h2>
      <p className="text-sm mb-4">
        You agree to indemnify, defend, and hold harmless FullPlateNow, its
        affiliates, officers, directors, employees, and agents from any claims,
        liabilities, damages, losses, or expenses (including reasonable
        attorneys’ fees) arising out of:
      </p>
      <ul className="text-sm mb-4 list-disc pl-5">
        <li>Your use of the Services in violation of these Terms.</li>
        <li>
          Your failure to fulfill orders or comply with Delivery Service
          agreements.
        </li>
        <li>
          Any content you upload to the Services, including claims of
          intellectual property infringement.
        </li>
        <li>
          Any disputes between you and the Delivery Services, including pricing,
          order fulfillment, or customer complaints.
        </li>
        <li>Your violation of any applicable law or regulation.</li>
      </ul>

      <h2 className="text-lg font-semibold mt-6 mb-2">13. Termination</h2>
      <h3 className="text-base font-semibold mt-4 mb-2">
        13.1 Termination by You
      </h3>
      <p className="text-sm mb-4">
        You may terminate your use of the Services at any time by closing your
        Account and ceasing access to the Website.
      </p>

      <h3 className="text-base font-semibold mt-4 mb-2">
        13.2 Termination by FullPlateNow
      </h3>
      <p className="text-sm mb-4">
        We may suspend or terminate your access to the Services at our sole
        discretion, with or without notice, if you violate these Terms, fail to
        comply with Delivery Service agreements, or engage in conduct that we
        deem harmful to our business interests.
      </p>

      <h3 className="text-base font-semibold mt-4 mb-2">
        13.3 Effect of Termination
      </h3>
      <p className="text-sm mb-4">
        Upon termination, your license to use the Services will cease, and you
        must stop accessing the Website. FullPlateNow will cease managing your
        operations on Delivery Services, but you remain responsible for
        fulfilling any outstanding orders. Sections 8 (Intellectual Property), 9
        (Data Privacy and Security), 10 (Confidentiality), 11 (Limitation of
        Liability), 12 (Indemnification), 14 (Governing Law), and 15
        (Miscellaneous) will survive termination.
      </p>

      <h2 className="text-lg font-semibold mt-6 mb-2">
        14. Governing Law and Dispute Resolution
      </h2>
      <h3 className="text-base font-semibold mt-4 mb-2">14.1 Governing Law</h3>
      <p className="text-sm mb-4">
        These Terms shall be governed by and construed in accordance with the
        laws of the State of Delaware, without regard to its conflict of law
        principles.
      </p>

      <h3 className="text-base font-semibold mt-4 mb-2">
        14.2 Dispute Resolution
      </h3>
      <p className="text-sm mb-4">
        Any dispute arising out of or relating to these Terms shall be resolved
        through binding arbitration in accordance with the rules of the American
        Arbitration Association (AAA). The arbitration shall take place in
        Wilmington, Delaware, and be conducted in English. The arbitrator’s
        decision shall be final and binding, and judgment may be entered in any
        court of competent jurisdiction. Notwithstanding the foregoing, either
        party may seek injunctive relief in a court of competent jurisdiction to
        prevent irreparable harm.
      </p>

      <h3 className="text-base font-semibold mt-4 mb-2">
        14.3 Class Action Waiver
      </h3>
      <p className="text-sm mb-4">
        You agree that any disputes will be resolved on an individual basis, and
        you waive any right to participate in a class action, collective action,
        or representative action against FullPlateNow.
      </p>

      <h2 className="text-lg font-semibold mt-6 mb-2">15. Miscellaneous</h2>
      <h3 className="text-base font-semibold mt-4 mb-2">
        15.1 Entire Agreement
      </h3>
      <p className="text-sm mb-4">
        These Terms, together with the Privacy Policy, constitute the entire
        agreement between you and FullPlateNow regarding your use of the
        Services, superseding any prior agreements or understandings.
      </p>

      <h3 className="text-base font-semibold mt-4 mb-2">15.2 Assignment</h3>
      <p className="text-sm mb-4">
        You may not assign or transfer your rights or obligations under these
        Terms without our prior written consent. FullPlateNow may assign its
        rights and obligations to any affiliate or successor without your
        consent.
      </p>

      <h3 className="text-base font-semibold mt-4 mb-2">15.3 Force Majeure</h3>
      <p className="text-sm mb-4">
        FullPlateNow shall not be liable for any failure to perform its
        obligations under these Terms due to events beyond its reasonable
        control, including but not limited to acts of God, natural disasters,
        war, terrorism, labor disputes, or government actions.
      </p>

      <h3 className="text-base font-semibold mt-4 mb-2">15.4 Severability</h3>
      <p className="text-sm mb-4">
        If any provision of these Terms is found to be invalid or unenforceable,
        the remaining provisions will remain in full force and effect.
      </p>

      <h3 className="text-base font-semibold mt-4 mb-2">15.5 Waiver</h3>
      <p className="text-sm mb-4">
        No waiver of any provision of these Terms shall be deemed a further or
        continuing waiver of such provision or any other provision.
      </p>

      <h3 className="text-base font-semibold mt-4 mb-2">15.6 Notices</h3>
      <p className="text-sm mb-4">
        Any notices required under these Terms shall be sent to FullPlateNow at
        [legal@fullplatenow.com] and to you at the email address associated with
        your Account. Notices are deemed received 24 hours after being sent.
      </p>

      <h3 className="text-base font-semibold mt-4 mb-2">
        15.7 Relationship of the Parties
      </h3>
      <p className="text-sm mb-4">
        You and FullPlateNow are independent contractors, and nothing in these
        Terms creates a partnership, joint venture, agency, or employment
        relationship between the parties.
      </p>

      <h3 className="text-base font-semibold mt-4 mb-2">
        15.8 Third-Party Beneficiaries
      </h3>
      <p className="text-sm mb-4">
        There are no third-party beneficiaries to these Terms, except as
        expressly provided herein with respect to Delivery Services.
      </p>

      <h2 className="text-lg font-semibold mt-6 mb-2">
        16. Contact Information
      </h2>
      <p className="text-sm mb-4">
        For questions or concerns regarding these Terms, please contact us at:
      </p>
      <p className="text-sm mb-4">
        FullPlateNow LLC <br />
        Email: legal@fullplatenow.com <br />
        Address: 8 The Green, Dover, DE 19901
      </p>

      <p className="text-xs text-black/50 mt-10">
        Effective date: April 21, 2025
      </p>
    </div>
  );
};

export default TermsOfService;
